import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import MobileLayout from '../../components/MobileLayout';

type Tier = 'free' | 'plus' | 'premium';

interface ModelOption {
  id: string;
  label: string;
  provider: 'openai' | 'xai';
  tier: Tier;
  /** The plan's tier covers this model. */
  allowed: boolean;
  /** The provider has an API key configured. */
  available: boolean;
}

interface ModelsResponse {
  models: ModelOption[];
  preferred: string | null;
  current: string;
  default: string;
}

const PLAN_DEFAULT = '__plan_default__';

const optionClass = (disabled: boolean) =>
  `flex items-start gap-3 w-full p-4 rounded-xl border text-left transition-colors ${
    disabled ? 'cursor-not-allowed' : 'cursor-pointer'
  }`;

const optionStyle = (checked: boolean) => ({
  borderColor: checked ? 'var(--ds-accent, #8b5cf6)' : 'var(--ds-border)',
  background: 'var(--ds-surface)',
});

const AiModel: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState<ModelsResponse | null>(null);
  const [selected, setSelected] = useState<string>(PLAN_DEFAULT);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get<ModelsResponse>('/ai/models')
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        const preferred = res.data.preferred;
        // A pick that left the catalog or the plan (downgrade, key pulled) cannot be re-saved,
        // so show the plan default instead of a checked-but-disabled radio.
        setSelected(
          preferred && res.data.models.some((m) => m.id === preferred && m.allowed && m.available)
            ? preferred
            : PLAN_DEFAULT,
        );
      })
      .catch(() => {
        if (!cancelled) {
          setLoadFailed(true);
          toast.error(t('aiModel.loadError', 'Could not load the model list'));
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [t]);

  const tierLabel = (tier: Tier) =>
    tier === 'free'
      ? t('aiModel.tierFree', 'Included')
      : tier === 'plus'
        ? t('aiModel.tierPlus', 'Advanced')
        : t('aiModel.tierPremium', 'Premium');

  const providerLabel = (provider: ModelOption['provider']) =>
    provider === 'xai' ? t('aiModel.providerXai', 'xAI') : t('aiModel.providerOpenai', 'OpenAI');

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put('/ai/models/preferred', {
        model: selected === PLAN_DEFAULT ? null : selected,
      });
      toast.success(t('aiModel.saved', 'Model saved'));
      navigate('/profile');
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || t('aiModel.saveError', 'Could not save your choice'),
      );
    } finally {
      setSaving(false);
    }
  };

  const defaultModel = data?.models.find((m) => m.id === data.default);

  return (
    <MobileLayout>
      <div className="flex-1 flex flex-col">
        <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--ds-border)' }}>
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => navigate('/profile')}
              aria-label={t('common.back', 'Back')}
              className="w-10 h-10 flex items-center justify-center rounded-full"
            >
              <ArrowLeftIcon className="w-5 h-5" style={{ color: 'var(--ds-text-primary)' }} />
            </button>
            <h1 className="ml-4 text-xl font-semibold" style={{ color: 'var(--ds-text-primary)' }}>
              {t('aiModel.title', 'AI model')}
            </h1>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div
              className="animate-spin rounded-full h-8 w-8 border-b-2"
              style={{ borderColor: 'var(--ds-accent, #8b5cf6)' }}
            />
          </div>
        ) : loadFailed || !data ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            <p className="mb-4" style={{ color: 'var(--ds-text-secondary)' }}>
              {t('aiModel.loadError', 'Could not load the model list')}
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-semibold"
            >
              {t('common.retry', 'Retry')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSave} className="flex-1 px-6 py-6 space-y-6">
            <p className="text-sm" style={{ color: 'var(--ds-text-secondary)' }}>
              {t(
                'aiModel.intro',
                'Choose which model writes with you. Your plan decides which ones are unlocked.',
              )}
            </p>

            <fieldset className="space-y-3">
              <legend className="sr-only">{t('aiModel.title', 'AI model')}</legend>

              <label className={optionClass(false)} style={optionStyle(selected === PLAN_DEFAULT)}>
                <input
                  type="radio"
                  name="model"
                  value={PLAN_DEFAULT}
                  checked={selected === PLAN_DEFAULT}
                  onChange={() => setSelected(PLAN_DEFAULT)}
                  className="mt-1 accent-primary-500"
                />
                <span className="flex-1">
                  <span className="block font-medium" style={{ color: 'var(--ds-text-primary)' }}>
                    {t('aiModel.planDefault', 'Plan default')}
                  </span>
                  <span className="block text-xs" style={{ color: 'var(--ds-text-secondary)' }}>
                    {t(
                      'aiModel.planDefaultHint',
                      'Always the recommended model for your plan ({{model}})',
                      { model: defaultModel?.label ?? data.default },
                    )}
                  </span>
                </span>
              </label>

              {data.models.map((m) => {
                const checked = selected === m.id;
                const isCurrent = data.current === m.id;
                const selectable = m.allowed && m.available;
                return (
                  <div key={m.id}>
                    <label className={optionClass(!selectable)} style={optionStyle(checked)}>
                      <input
                        type="radio"
                        name="model"
                        value={m.id}
                        checked={checked}
                        disabled={!selectable}
                        onChange={() => setSelected(m.id)}
                        className="mt-1 accent-primary-500"
                      />
                      <span className="flex-1">
                        <span className="flex items-center gap-2">
                          <span className="font-medium" style={{ color: 'var(--ds-text-primary)' }}>
                            {m.label}
                          </span>
                          <span
                            className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded"
                            style={{
                              background: 'var(--ds-surface-2, rgba(139,92,246,0.12))',
                              color: 'var(--ds-text-secondary)',
                            }}
                          >
                            {tierLabel(m.tier)}
                          </span>
                          {isCurrent && (
                            <span className="text-[10px] uppercase tracking-wide text-primary-500">
                              {t('aiModel.current', 'In use')}
                            </span>
                          )}
                        </span>
                        <span
                          className="block text-xs"
                          style={{ color: 'var(--ds-text-secondary)' }}
                        >
                          {providerLabel(m.provider)}
                          {!m.allowed && (
                            <>
                              {' · '}
                              <LockClosedIcon className="inline w-3 h-3 -mt-0.5" />{' '}
                              {t('aiModel.locked', 'Not on your plan')}
                            </>
                          )}
                          {m.allowed && !m.available && (
                            <>
                              {' · '}
                              {t('aiModel.unavailable', 'Temporarily unavailable')}
                            </>
                          )}
                        </span>
                      </span>
                    </label>
                    {!m.allowed && m.tier !== 'free' && (
                      <button
                        type="button"
                        onClick={() => navigate('/subscription')}
                        className="mt-1 ml-4 text-xs text-primary-500 underline"
                      >
                        {t('aiModel.upgrade', 'Upgrade')}
                      </button>
                    )}
                  </div>
                );
              })}
            </fieldset>

            <button
              type="submit"
              disabled={saving}
              className="w-full py-4 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 text-white font-semibold rounded-xl transition-colors"
            >
              {saving ? t('aiModel.saving', 'Saving…') : t('aiModel.save', 'Save')}
            </button>
          </form>
        )}
      </div>
    </MobileLayout>
  );
};

export default AiModel;
